import CalendarView from '@components/containers/replenishment-planning/engine/CalendarView';
import ObjectivesView from '@components/containers/replenishment-planning/engine/ObjectivesView';
import InventoryHealthView from '@components/containers/replenishment-planning/InventoryHealthView';
import TrackView from '@components/containers/replenishment-planning/TrackView';
import AppLayout from '@layouts/AppLayout';
import { Group, SegmentedControl, Stack } from '@mantine/core';
import { type FC } from 'react';
import { useSearchParams } from 'react-router';

type Mode = 'health' | 'plan' | 'calendar' | 'track';

const modeOf = (params: URLSearchParams): Mode => {
    const tab = params.get('tab');
    if (tab === 'ask' || tab === 'plan') return 'plan';
    if (tab === 'calendar' || tab === 'track' || tab === 'health') return tab;
    return params.get('ask') || params.get('q') || params.get('intent') || params.get('plan') ? 'plan' : 'health';
};

// Four surfaces, toggled with the same SegmentedControl as distribution:
//   "Inventory Health" — today's actuals vs each SKU's reorder point. Free to open.
//   "Plan" — the brand's objectives as tabs over the latest engine run: what to replenish,
//       produce or order, approvable per row. Objectives are set in its Settings.
//   "Calendar" — the plan by place-by day: runs to start, orders to place, transfers to send.
//   "Track" — placed supply (mother orders + work orders, in-production / in-transit).
const Replenishment: FC = () => {
    // The surface lives in the URL (`?tab=`), so a scheduled email of a table reopens the
    // surface it is on rather than Inventory Health.
    const [params, setParams] = useSearchParams();
    const mode = modeOf(params);
    const setMode = (next: Mode): void =>
        setParams(
            (prev) => {
                const out = new URLSearchParams(prev);
                out.set('tab', next);
                return out;
            },
            { replace: true },
        );
    return (
        <AppLayout>
            <Stack gap={8}>
                <Group px="md" pt="md">
                    <SegmentedControl
                        size="xs"
                        value={mode}
                        onChange={(v) => setMode(v as Mode)}
                        data={[
                            { value: 'health', label: 'Inventory Health' },
                            { value: 'plan', label: 'Plan' },
                            { value: 'calendar', label: 'Calendar' },
                            { value: 'track', label: 'Track' },
                        ]}
                    />
                </Group>
                {mode === 'health' && (
                    <InventoryHealthView onPlanForSkus={() => setMode('plan')} />
                )}
                {mode === 'plan' && <ObjectivesView />}
                {mode === 'calendar' && <CalendarView />}
                {mode === 'track' && <TrackView />}
            </Stack>
        </AppLayout>
    );
};

export default Replenishment;
